import {
  Chat,
  type ChatIdentity,
  type ChatMessage,
  ChatStore,
  type ReactionSummary,
  type RoomLike,
} from "@codeshare/chat";

export interface ChatState {
  /** False when not in a room. */
  active: boolean;
  messages: readonly ChatMessage[];
  /** Message id → its reactions, grouped by emoji. */
  reactions: ReadonlyMap<string, ReactionSummary[]>;
  typing: { clientId: string; name: string }[];
  /** Messages from others since the chat was last viewed. */
  unread: number;
}

const EMPTY: ChatState = {
  active: false,
  messages: [],
  reactions: new Map(),
  typing: [],
  unread: 0,
};

/** The current room's chat (or none), with unread tracking for the UI. */
export class ChatSession {
  #chat: Chat | undefined;
  #store: ChatStore | undefined;
  #state: ChatState = EMPTY;
  #visible = false;
  #seen = 0;
  #identity: ChatIdentity;
  readonly #listeners = new Set<() => void>();
  #unsubscribe: (() => void) | undefined;

  constructor(identity: ChatIdentity) {
    this.#identity = identity;
  }

  get state(): ChatState {
    return this.#state;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  async join(room: RoomLike, topic: string): Promise<void> {
    this.leave();
    let store: ChatStore;
    try {
      store = await ChatStore.open(topic);
    } catch {
      store = ChatStore.memory(); // IndexedDB unavailable: chat still works, just not saved
    }
    this.#store = store;
    this.#chat = new Chat({ room, store, identity: this.#identity });
    this.#seen = this.#othersCount();
    this.#unsubscribe = this.#chat.subscribe(() => this.#update());
    this.#update();
  }

  leave(): void {
    this.#unsubscribe?.();
    this.#chat?.destroy();
    this.#store?.close();
    this.#chat = undefined;
    this.#store = undefined;
    this.#set(EMPTY);
  }

  send(text: string): void {
    this.#chat?.send(text);
  }

  /** Adds or takes back this user's emoji reaction on a message. */
  react(msgId: string, emoji: string): void {
    this.#chat?.react(msgId, emoji);
  }

  setTyping(on: boolean): void {
    this.#chat?.setTyping(on);
  }

  setIdentity(identity: ChatIdentity): void {
    this.#identity = identity;
    this.#chat?.setIdentity(identity);
  }

  /** Whether the chat is on screen; while it is, everything counts as read. */
  setVisible(visible: boolean): void {
    this.#visible = visible;
    this.#update();
  }

  #othersCount(): number {
    return (this.#chat?.messages() ?? []).filter((m) => m.clientId !== this.#identity.clientId)
      .length;
  }

  #update(): void {
    if (!this.#chat) return;
    const others = this.#othersCount();
    if (this.#visible) this.#seen = others;
    this.#set({
      active: true,
      messages: this.#chat.messages(),
      reactions: this.#chat.reactions(),
      typing: this.#chat.typing(),
      unread: Math.max(0, others - this.#seen),
    });
  }

  #set(state: ChatState): void {
    this.#state = state;
    for (const listener of this.#listeners) listener();
  }
}
